import * as React from 'react';
import { clsx } from 'clsx';

export interface TabItem {
  /**
   * The label text for the tab
   */
  label: string;
  /**
   * Optional icon component
   */
  icon?: React.ReactNode;
  /**
   * Value to identify this tab
   */
  value: string;
}

export interface TabsProps {
  /**
   * Array of tab items to display
   */
  tabs: TabItem[];
  /**
   * Currently active tab value
   */
  activeTab?: string;
  /**
   * Callback when tab is clicked
   */
  onTabChange?: (value: string) => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const tabsContainerStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  borderBottom: '1px solid #f8f8f8',
  paddingBottom: '1px',
  width: '100%',
};

const tabItemWrapperStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
  position: 'relative',
};

const tabItemStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '8px 12px',
  borderRadius: '8px',
  fontSize: '14px',
  lineHeight: '1.5',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  outline: 'none',
  transition: 'all 0.2s ease-in-out',
  userSelect: 'none' as const,
};

const underlineStyles: React.CSSProperties = {
  width: '100%',
  height: '2px',
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
  transition: 'background-color 0.2s ease-in-out',
};

/**
 * Tabs component - Arbor Design System
 *
 * Horizontal tabs with support for icons and various states.
 */
export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      tabs,
      activeTab,
      onTabChange,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [hoveredTab, setHoveredTab] = React.useState<string | null>(null);
    const [focusedTab, setFocusedTab] = React.useState<string | null>(null);

    // Determine the active tab (first tab by default)
    const currentActiveTab = activeTab || (tabs.length > 0 ? tabs[0].value : '');

    const handleTabClick = (value: string) => {
      onTabChange?.(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent, value: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleTabClick(value);
      }
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-tabs', className)}
        style={{ ...tabsContainerStyles, ...style }}
        data-testid={dataTestId}
        role="tablist"
      >
        {tabs.map((tab) => {
          const isActive = tab.value === currentActiveTab;
          const isHovered = tab.value === hoveredTab;
          const isFocused = tab.value === focusedTab;

          // Determine text color
          const getTextColor = (): string => {
            if (isActive) {
              return '#0e8a0e'; // Active green
            }
            if (isHovered) {
              return '#202020'; // Hover darker grey
            }
            return '#2f2f2f'; // Default grey
          };

          // Determine font weight
          const getFontWeight = (): number => {
            return isActive ? 600 : 500; // Semibold for active, medium for inactive
          };

          // Determine underline color
          const getUnderlineColor = (): string => {
            if (isActive) {
              return '#0e8a0e'; // Active green
            }
            if (isHovered) {
              return '#d1d1d1'; // Hover grey
            }
            return 'transparent'; // Default transparent
          };

          // Get box shadow for focus ring
          const getBoxShadow = (): string | undefined => {
            if (isFocused) {
              return '0px 0px 0px 3px #3cad51';
            }
            return undefined;
          };

          const tabItemStylesCombined: React.CSSProperties = {
            ...tabItemStyles,
            color: getTextColor(),
            fontWeight: getFontWeight(),
            boxShadow: getBoxShadow(),
            backgroundColor: isFocused ? 'rgba(255, 255, 255, 0.01)' : 'transparent',
          };

          const underlineStylesCombined: React.CSSProperties = {
            ...underlineStyles,
            backgroundColor: getUnderlineColor(),
          };

          return (
            <div
              key={tab.value}
              style={tabItemWrapperStyles}
              onMouseEnter={() => setHoveredTab(tab.value)}
              onMouseLeave={() => setHoveredTab(null)}
            >
              <div
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => handleTabClick(tab.value)}
                onKeyDown={(e) => handleKeyDown(e, tab.value)}
                onFocus={() => setFocusedTab(tab.value)}
                onBlur={() => setFocusedTab(null)}
                style={tabItemStylesCombined}
              >
                {tab.icon && (
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    {tab.icon}
                  </span>
                )}
                <span>{tab.label}</span>
              </div>
              <div style={underlineStylesCombined} />
            </div>
          );
        })}
      </div>
    );
  }
);

Tabs.displayName = 'Tabs';
